import React, { useState, useEffect, useCallback } from 'react'; // 1. Import useCallback
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const DashboardPage = () => {
  const { user } = useAuth();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 2. Wrap the function in useCallback and list its own dependencies
  const loadUserQuestionnaires = useCallback(async () => {
    setLoading(true);
    try {
      const userId = user?.id;
      if (!userId) {
        setError('Utilisateur non identifié');
        return;
      }

      const apiUrl =
        process.env.NODE_ENV === 'development'
          ? 'http://localhost:7071/api/getUserQuestionnaires'
          : '/api/getUserQuestionnaires';

      const response = await axios.get(apiUrl, {
        params: { userId },
      });

      setQuestionnaires(response.data.questionnaires || []);
    } catch (err) {
      console.error('Erreur lors du chargement des questionnaires:', err);
      setError('Erreur lors du chargement de vos questionnaires');
    } finally {
      setLoading(false);
    }
  }, [user]); // <-- The function depends on `user`, so list it here.

  // 3. This useEffect is now correct. It will only run when loadUserQuestionnaires changes (which is only when `accounts` changes).
  useEffect(() => {
    loadUserQuestionnaires();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loadUserQuestionnaires]);

  const deleteProgress = async (questionnaireId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette progression ?')) {
      return;
    }

    try {
      const userId = user?.id;
      await axios.delete('/api/deleteProgress', {
        data: { userId, questionnaireId },
      });

      // Recharger la liste
      loadUserQuestionnaires();
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: { backgroundColor: '#27ae60', color: 'white' },
      in_progress: { backgroundColor: '#f39c12', color: 'white' },
      not_started: { backgroundColor: '#95a5a6', color: 'white' },
    };

    const labels = {
      completed: 'Terminé',
      in_progress: 'En cours',
      not_started: 'Non commencé',
    };

    return (
      <span
        style={{
          ...styles[status],
          padding: '0.25rem 0.75rem',
          borderRadius: '12px',
          fontSize: '0.85rem',
          fontWeight: '500',
        }}
      >
        {labels[status]}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="loading">Chargement de votre tableau de bord...</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ marginTop: '2rem' }}>
      <div className="card">
        <h1 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
          📊 Tableau de Bord
        </h1>
        <p style={{ color: '#7f8c8d', marginBottom: '2rem' }}>
          Bienvenue {user?.name || user?.email} ! Retrouvez
          ici tous vos questionnaires et votre progression.
        </p>

        {error && <div className="error">{error}</div>}

        {/* Statistiques rapides */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem',
          }}
        >
          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📝</div>
            <div
              style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#2c3e50',
              }}
            >
              {questionnaires.length}
            </div>
            <div style={{ color: '#7f8c8d' }}>Questionnaires</div>
          </div>

          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
            <div
              style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#27ae60',
              }}
            >
              {questionnaires.filter((q) => q.status === 'completed').length}
            </div>
            <div style={{ color: '#7f8c8d' }}>Terminés</div>
          </div>

          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
            <div
              style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#f39c12',
              }}
            >
              {questionnaires.filter((q) => q.status === 'in_progress').length}
            </div>
            <div style={{ color: '#7f8c8d' }}>En cours</div>
          </div>
        </div>

        {/* Liste des questionnaires */}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
            }}
          >
            <h2 style={{ color: '#2c3e50', margin: 0 }}>
              Mes Questionnaires
            </h2>
            <Link to="/" className="btn btn-primary">
              + Nouveau Questionnaire
            </Link>
          </div>

          {questionnaires.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
              <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
                Aucun questionnaire trouvé
              </h3>
              <p style={{ color: '#7f8c8d', marginBottom: '2rem' }}>
                Commencez votre premier questionnaire pour découvrir vos dons
                spirituels !
              </p>
              <Link to="/" className="btn btn-primary">
                Commencer un questionnaire
              </Link>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {questionnaires.map((questionnaire) => (
                <div
                  key={`${questionnaire.questionnaireId}-${questionnaire.lastUpdated}`}
                  className="card"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <h3 style={{ margin: 0, color: '#2c3e50' }}>
                        {questionnaire.title ||
                          'Questionnaire des Dons Spirituels'}
                      </h3>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {questionnaire.questionnaireId.includes('court') && (
                          <span
                            style={{
                              backgroundColor: '#27ae60',
                              color: 'white',
                              padding: '3px 8px',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                            }}
                          >
                            ⚡ RAPIDE
                          </span>
                        )}
                        {questionnaire.questionnaireId.includes('long') && (
                          <span
                            style={{
                              backgroundColor: '#3498db',
                              color: 'white',
                              padding: '3px 8px',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                            }}
                          >
                            🔍 COMPLET
                          </span>
                        )}
                        {getStatusBadge(questionnaire.status)}
                      </div>
                    </div>

                    <div style={{ color: '#7f8c8d', marginBottom: '0.5rem' }}>
                      Progression: {questionnaire.answeredQuestions || 0} /{' '}
                      {questionnaire.totalQuestions || (questionnaire.questionnaireId.includes('court') ? 80 : 160)} questions
                    </div>

                    {questionnaire.progress && (
                      <div
                        className="progress-bar"
                        style={{ width: '200px', marginBottom: '0.5rem' }}
                      >
                        <div
                          className="progress-fill"
                          style={{
                            width: `${(questionnaire.answeredQuestions /
                              questionnaire.totalQuestions) *
                              100
                              }%`,
                          }}
                        ></div>
                      </div>
                    )}

                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                      Dernière modification:{' '}
                      {formatDate(questionnaire.lastUpdated)}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'center',
                    }}
                  >
                    {questionnaire.status === 'completed' ? (
                      <>
                        <Link
                          to={`/results/${questionnaire.questionnaireId}`}
                          className="btn btn-success"
                        >
                          Voir les résultats
                        </Link>
                        <Link
                          to={`/review/${questionnaire.questionnaireId}`}
                          className="btn btn-info"
                        >
                          Réviser les réponses
                        </Link>
                      </>
                    ) : (
                      <Link
                        to={`/questionnaire/${questionnaire.questionnaireId}`}
                        className="btn btn-primary"
                      >
                        {questionnaire.status === 'in_progress'
                          ? 'Continuer'
                          : 'Commencer'}
                      </Link>
                    )}

                    <button
                      onClick={() =>
                        deleteProgress(questionnaire.questionnaireId)
                      }
                      className="btn btn-secondary"
                      style={{ backgroundColor: '#e74c3c' }}
                      title="Supprimer la progression"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions rapides */}
        <div style={{ marginTop: '3rem', textAlign: 'center' }}>
          <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            Actions rapides
          </h3>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Link to="/" className="btn btn-primary">
              📋 Nouveaux questionnaires
            </Link>
            <button
              onClick={loadUserQuestionnaires}
              className="btn btn-secondary"
            >
              🔄 Actualiser
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
